/** Brain tab — web mirror of `Sources/Views/BrainView.swift`.
 *
 * A searchable, type-filterable list of the caller's memories, read through
 * `AppEnvironment.memories` (MockMemoriesStore offline; BrainMemoriesStore against
 * the Worker's read-only memories REST API in the live path). Selecting a row
 * opens a detail pane with the full content + frontmatter.
 *
 * Memories are NOT part of the polled factory snapshot, so this view owns its own
 * load/search state. Search is debounced and routed to GET /memories/search; the
 * type filter maps to the `type` query param. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEnv } from '../app/context';
import {
  memoryType,
  memoryTypeColor,
  memoryTypeIcon,
  memoryTypeLabel,
  SELECTABLE_MEMORY_TYPES,
  type Memory,
  type MemorySummary,
  type MemoryType,
} from '../models/memory';
import { jsonDisplayString } from '../models/factory';
import { EmptyState, relativeShort, Section, StatusPill } from '../components/UIHelpers';
import { FilesView } from './FilesView';
import { SessionsView } from './SessionsView';

type BrainSection = 'memories' | 'files' | 'sessions';

const BRAIN_SECTIONS: Array<{ id: BrainSection; label: string; icon: string }> = [
  { id: 'memories', label: 'Memories', icon: '🧠' },
  { id: 'files', label: 'Files', icon: '📁' },
  { id: 'sessions', label: 'Sessions', icon: '🖥' },
];

/** Brain tab shell — mirror of the Swift `BrainView` segmented container. A
 * sub-tab bar switches between three owner surfaces: Memories (multi-tenant,
 * read-only), Files, and Sessions (both owner-only Mac control via the bridge).
 * Each section owns its own load state, so switching never disturbs the others. */
export function BrainView() {
  const [section, setSection] = useState<BrainSection>('memories');
  return (
    <>
      <div className="chip-row seg-row" role="tablist" aria-label="Brain section">
        {BRAIN_SECTIONS.map((s) => (
          <button
            key={s.id}
            className={`chip${section === s.id ? ' chip--active' : ''}`}
            role="tab"
            aria-selected={section === s.id}
            onClick={() => setSection(s.id)}
          >
            <span aria-hidden>{s.icon}</span> {s.label}
          </button>
        ))}
      </div>
      {section === 'memories' ? <MemoriesView /> : null}
      {section === 'files' ? <FilesView /> : null}
      {section === 'sessions' ? <SessionsView /> : null}
    </>
  );
}

/** The memories surface (formerly `BrainView`). */
function MemoriesView() {
  const env = useEnv();
  const [summaries, setSummaries] = useState<MemorySummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<MemoryType | null>(null);
  const [selected, setSelected] = useState<MemorySummary | null>(null);
  const hasLoadedOnce = useRef(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const q = search.trim();
    try {
      const result = q
        ? await env.memories.search({ query: q, type: typeFilter ?? undefined })
        : await env.memories.list({ type: typeFilter ?? undefined });
      setSummaries(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSummaries([]);
    } finally {
      setIsLoading(false);
      hasLoadedOnce.current = true;
    }
  }, [env, search, typeFilter]);

  // Debounced reload on any search/filter change (immediate on first mount).
  useEffect(() => {
    if (!hasLoadedOnce.current) {
      void load();
      return;
    }
    const t = setTimeout(() => void load(), 300);
    return () => clearTimeout(t);
  }, [load]);

  const groups = useMemo(() => groupByType(summaries), [summaries]);

  if (selected) {
    return <MemoryDetail summary={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <>
      <div className="brain__search">
        <input
          value={search}
          placeholder="Search memories…"
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search memories"
        />
      </div>

      <div className="chip-row" role="tablist" aria-label="Filter by type">
        <button
          className={`chip${typeFilter === null ? ' chip--active' : ''}`}
          aria-selected={typeFilter === null}
          onClick={() => setTypeFilter(null)}
        >
          All
        </button>
        {SELECTABLE_MEMORY_TYPES.map((t) => (
          <button
            key={t}
            className={`chip${typeFilter === t ? ' chip--active' : ''}`}
            aria-selected={typeFilter === t}
            onClick={() => setTypeFilter(typeFilter === t ? null : t)}
          >
            <span aria-hidden>{memoryTypeIcon(t)}</span> {memoryTypeLabel(t)}
          </button>
        ))}
      </div>

      {isLoading && summaries.length === 0 ? (
        <div className="spinner" />
      ) : error && summaries.length === 0 ? (
        <EmptyState icon="⚠" title="Couldn't load memories" message={error} />
      ) : summaries.length === 0 ? (
        <EmptyState
          icon="🧠"
          title={search ? 'No matches' : 'No memories'}
          message={
            search ? `Nothing matches “${search}”.` : 'Memories written via the Brain will appear here.'
          }
        />
      ) : (
        groups.map(([type, rows]) => (
          <Section key={type} header={`${memoryTypeLabel(type)} · ${rows.length}`}>
            {rows.map((m) => (
              <MemoryRow key={m.id} memory={m} onOpen={() => setSelected(m)} />
            ))}
          </Section>
        ))
      )}
      <div style={{ height: 24 }} />
    </>
  );
}

function MemoryRow({ memory, onOpen }: { memory: MemorySummary; onOpen: () => void }) {
  const t = memoryType(memory.type);
  return (
    <button className="row row--tappable" onClick={onOpen}>
      <span aria-hidden style={{ width: 22, textAlign: 'center' }}>
        {memoryTypeIcon(t)}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row__head">
          <div className="row__title" style={{ fontSize: 14, flex: 1, minWidth: 0 }}>
            {memory.name}
          </div>
          <span className="caption">{relativeShort(memory.updatedAt)}</span>
        </div>
        {memory.description ? <div className="row__subtitle">{memory.description}</div> : null}
      </div>
    </button>
  );
}

function MemoryDetail({ summary, onBack }: { summary: MemorySummary; onBack: () => void }) {
  const env = useEnv();
  const [memory, setMemory] = useState<Memory | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = memoryType(summary.type);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    env.memories
      .get(summary.id)
      .then((m) => {
        if (!cancelled) setMemory(m);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [env, summary.id]);

  const frontmatter =
    memory?.frontmatter && typeof memory.frontmatter === 'object' && !Array.isArray(memory.frontmatter)
      ? (memory.frontmatter as Record<string, unknown>)
      : null;

  return (
    <>
      <div className="detail-bar">
        <button className="detail-bar__back" onClick={onBack}>
          ‹ Brain
        </button>
      </div>

      <Section header="Memory">
        <div className="row">
          <div className="row__title">{summary.name}</div>
          <div className="row__head" style={{ marginTop: 8, gap: 6, flexWrap: 'wrap' }}>
            <StatusPill text={memoryTypeLabel(t)} color={memoryTypeColor(t)} icon={memoryTypeIcon(t)} />
            {summary.workspace ? <StatusPill text={summary.workspace} color="var(--indigo)" icon="🗄" /> : null}
          </div>
          {summary.description ? (
            <div className="caption" style={{ marginTop: 8, fontSize: 13 }}>
              {summary.description}
            </div>
          ) : null}
          <div className="caption" style={{ marginTop: 6 }}>
            Updated {relativeShort(summary.updatedAt)}
          </div>
        </div>
      </Section>

      <Section header="Content">
        <div className="row">
          {isLoading && !memory ? (
            <span className="caption">Loading…</span>
          ) : error && !memory ? (
            <span className="caption">⚠ {error}</span>
          ) : memory?.content ? (
            <pre className="detail-content">{memory.content}</pre>
          ) : (
            <span className="caption">No content.</span>
          )}
        </div>
      </Section>

      {frontmatter && Object.keys(frontmatter).length > 0 ? (
        <Section header="Frontmatter">
          {Object.keys(frontmatter)
            .sort()
            .map((key) => (
              <div className="row row__head" key={key}>
                <span className="mono" style={{ flex: 1 }}>
                  {key}
                </span>
                <span className="caption" style={{ textAlign: 'right' }}>
                  {jsonDisplayString(frontmatter[key] as never)}
                </span>
              </div>
            ))}
        </Section>
      ) : null}
      <div style={{ height: 24 }} />
    </>
  );
}

/** Group summaries by type, in canonical order (matches the Swift view). */
function groupByType(summaries: MemorySummary[]): Array<[MemoryType, MemorySummary[]]> {
  const order: MemoryType[] = [...SELECTABLE_MEMORY_TYPES, 'unknown'];
  const buckets = new Map<MemoryType, MemorySummary[]>();
  for (const m of summaries) {
    const t = memoryType(m.type);
    const arr = buckets.get(t) ?? [];
    arr.push(m);
    buckets.set(t, arr);
  }
  return order
    .map((t) => [t, buckets.get(t) ?? []] as [MemoryType, MemorySummary[]])
    .filter(([, rows]) => rows.length > 0);
}
